import type { RoutedActionHandler } from "../../app/actions.ts";
import type { TaskListMode } from "../../domain/app-types.ts";
import type { CollectionViewMode } from "../../domain/entities.ts";

export type BulkTaskAction = "bulk-complete" | "bulk-reopen" | "bulk-pin" | "bulk-unpin" | "bulk-archive" | "bulk-delete";

export interface BoardListActionCapabilities {
  setCollectionView(mode: CollectionViewMode): void | Promise<void>;
  setTaskListMode(mode: TaskListMode): void | Promise<void>;
  setBoardCardMode(mode: "simple" | "detailed"): void | Promise<void>;
  toggleInlineCapture(collectionId: string, column: string): void | Promise<void>;
  cancelInlineCapture(): void | Promise<void>;
  expandInlineCapture(form: HTMLFormElement): void | Promise<void>;
  toggleColumn(collectionId: string, column: string): void | Promise<void>;
  collapseOtherColumns(collectionId: string): void | Promise<void>;
  expandColumns(collectionId: string): void | Promise<void>;
  showMoreBoardTasks(collectionId: string, column: string): void | Promise<void>;
  setPage(page: number): void | Promise<void>;
  scroll(edge: "top" | "bottom"): void;
  toggleSelectionMode(): void | Promise<void>;
  selectVisible(ids: string[]): void | Promise<void>;
  bulkAction(action: BulkTaskAction): void | Promise<void>;
  bulkEdit(): void | Promise<void>;
  bulkMove(): void | Promise<void>;
}

const bulkActions = new Set<BulkTaskAction>(["bulk-complete", "bulk-reopen", "bulk-pin", "bulk-unpin", "bulk-archive", "bulk-delete"]);
const names = new Set(["collection-view", "task-list-mode", "board-card-mode", "inline-board-add", "cancel-board-capture", "expand-board-capture", "toggle-board-column", "collapse-other-board-columns", "expand-board-columns", "show-more-board-tasks", "task-page", "scroll-top", "scroll-bottom", "toggle-selection-mode", "select-visible-tasks", "bulk-edit", "bulk-move", ...bulkActions]);

export function createBoardListActionHandler(capabilities: BoardListActionCapabilities): RoutedActionHandler {
  return async (name, target) => {
    if (!names.has(name)) return false;
    if (name === "collection-view" && (target.dataset.mode === "list" || target.dataset.mode === "board" || target.dataset.mode === "timeline" || target.dataset.mode === "table")) await capabilities.setCollectionView(target.dataset.mode);
    if (name === "task-list-mode") await capabilities.setTaskListMode(target.dataset.mode === "simple" ? "simple" : "detailed");
    if (name === "board-card-mode") await capabilities.setBoardCardMode(target.dataset.mode === "simple" ? "simple" : "detailed");
    if (name === "inline-board-add") await capabilities.toggleInlineCapture(target.dataset.collectionId || "", target.dataset.column || "");
    if (name === "cancel-board-capture") await capabilities.cancelInlineCapture();
    if (name === "expand-board-capture") { const form = target.closest<HTMLFormElement>("[data-board-capture]"); if (form) await capabilities.expandInlineCapture(form); }
    if (name === "toggle-board-column") await capabilities.toggleColumn(target.dataset.collectionId || "", target.dataset.column || "");
    if (name === "collapse-other-board-columns") await capabilities.collapseOtherColumns(target.dataset.id || "");
    if (name === "expand-board-columns") await capabilities.expandColumns(target.dataset.id || "");
    if (name === "show-more-board-tasks") await capabilities.showMoreBoardTasks(target.dataset.collectionId || "", target.dataset.column || "");
    if (name === "task-page") await capabilities.setPage(Number(target.dataset.page) || 1);
    if (name === "scroll-top") capabilities.scroll("top");
    if (name === "scroll-bottom") capabilities.scroll("bottom");
    if (name === "toggle-selection-mode") await capabilities.toggleSelectionMode();
    if (name === "select-visible-tasks") {
      const ids = Array.from(document.querySelectorAll<HTMLInputElement>("[data-select-task]:not(:disabled)"))
        .filter(input => !input.closest(".kanban-column.is-collapsed"))
        .map(input => input.dataset.selectTask || "")
        .filter(Boolean);
      await capabilities.selectVisible(ids);
    }
    if (bulkActions.has(name as BulkTaskAction)) await capabilities.bulkAction(name as BulkTaskAction);
    if (name === "bulk-edit") await capabilities.bulkEdit();
    if (name === "bulk-move") await capabilities.bulkMove();
    return true;
  };
}
